import React, { useCallback, useEffect } from 'react';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { coreCatalogApi } from '@cp/entities/catalog';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { SwipeableCarouselWithAssessmentProps } from '@cp/ds/src/components/swipeableCarouselWithAssessment';
import { useIsMobile } from '@cp/ds/src/hooks';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { Container } from '@cp/ds/src/components/container';
import { Box } from '@cp/ds/src/components/box';
import { currentClientFavoriteApi } from '@cp/entities/client';
import { ClientType } from '@common/model/client/clientType';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { useInjection } from '@cp/shared/di';
import { CatalogItemModalViewType } from 'src/entities/catalog/item/ui';
import { CatalogItemModalView, CatalogItemViewContext, catalogItemSlice, useCatalogItem } from 'src/entities/catalog/item';
import { useClientProfile } from 'src/entities/client';
import { useIsAnyModalOpen } from 'src/shared/modalProvider';
import { AUTH_FEATURE } from '../di';
import {
  catalogItemViewListInstructionCompletedByRegisteredUserSelector,
  catalogItemViewListInstructionCompletedSelector,
  catalogItemViewSlice,
} from '../model/store';
import {
  CatalogItemViewListItemData,
  CatalogItemViewProps,
  CatalogItemViewPropsBase,
  CatalogItemViewPropsList,
  CatalogItemViewPropsSingle,
} from './types';

export * from './types';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const CatalogItemViewSingle = <DataType extends unknown>(
  props: CatalogItemViewPropsBase<DataType> & CatalogItemViewPropsSingle,
): ReturnType<React.FC> => {
  const { data, isLoading } = coreCatalogItemApi.endpoints.getById.useQuery({ id: props.id }, { skip: !props.id });

  return (
    <CatalogItemModalView
      {...props}
      viewProps={{
        data: data?.id !== props.id ? undefined : data,
        isLoading,
      }}
    />
  );
};

const CatalogItemViewListItem: SwipeableCarouselWithAssessmentProps<CatalogItemViewListItemData>['itemComponent'] = ({
  data: { listRequest, catalogItemType },
}) => {
  const isMobile = useIsMobile();
  const { data, isLoading } = coreCatalogApi.endpoints.getItems.useQuery(listRequest);
  const views = React.useContext(CatalogItemViewContext);

  const View = views[catalogItemType];
  const isVacancy = catalogItemType === CatalogItemType.Vacancy;
  // TODO: move this logic to VacancyView entity
  const ViewContainer = isMobile && isVacancy ? Container : Box;

  return (
    <ViewContainer>
      <View data={data?.items[0]} isLoading={isLoading} />
    </ViewContainer>
  );
};

const ASSESSMENTS_COUNT_BEFORE_TRIGGER_LOGIN = 3;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const CatalogItemViewList = <DataType extends unknown>(
  props: CatalogItemViewPropsBase<DataType> & CatalogItemViewPropsList,
): ReturnType<React.FC> => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const clientProfile = useClientProfile();
  const isInstructionCompleted = useSelector(catalogItemViewListInstructionCompletedSelector);
  const isInstructionCompletedByRegisteredUser = useSelector(catalogItemViewListInstructionCompletedByRegisteredUserSelector);
  const [itemsData, setItemsData] = React.useState<CatalogItemViewListItemData[]>();
  const authFeature = useInjection(AUTH_FEATURE);

  /**
   * Business mechanics for assessments
   */
  const assessmentsCountRef = React.useRef(0);
  const createAssessmentHandler =
    (type: 'like' | 'dislike'): SwipeableCarouselWithAssessmentProps<CatalogItemViewListItemData>['onLike'] =>
    async ({ data }) => {
      /**
       * Show login/registration
       * @see https://ru.yougile.com/team/2c7dacf7a810/#YOU-2783 (pt. 4)
       */
      assessmentsCountRef.current++;
      if (!clientProfile && assessmentsCountRef.current >= ASSESSMENTS_COUNT_BEFORE_TRIGGER_LOGIN) {
        return authFeature();
      }

      /**
       * Add to favorites
       * @see https://ru.yougile.com/team/2c7dacf7a810/#YOU-2783 (pt. 5)
       */
      if (type === 'like' && clientProfile?.type === ClientType.Employer && data.catalogItemType === CatalogItemType.Resume) {
        const response = await dispatch(coreCatalogApi.endpoints.getItems.initiate(data.listRequest)).unwrap();
        const clientId = response.items[0]?.clientProfile?.id;
        if (clientId) {
          dispatch(currentClientFavoriteApi.endpoints.createFavoriteClient.initiate({ clientId }));
        }
      }
    };
  React.useEffect(() => {
    // Reset assessments count on modal view close
    if (!props.id) {
      assessmentsCountRef.current = 0;
    }
  }, [props.id]);

  const handleInstructionCompleted = React.useCallback(() => {
    if (clientProfile) {
      dispatch(catalogItemViewSlice.actions.setListInstructionCompletedByRegisteredUser(true));
    } else {
      dispatch(catalogItemViewSlice.actions.setListInstructionCompleted(true));
    }
  }, [dispatch, clientProfile]);

  React.useEffect(() => {
    let isEffectActive = true;

    (async () => {
      const response = await dispatch(coreCatalogApi.endpoints.getItems.initiate(props.listRequest)).unwrap();

      if (!response.items.length) {
        throw new Error(
           
          `[CatalogItemViewSwipeableList]: list request is invalid - received empty response! (Request: ${JSON.stringify(props.listRequest)})`,
        );
      }

      if (!isEffectActive) {
        return;
      }

      const calcPage = (page: number, pagination: number): number => {
        const skip = (page - 1) * pagination;
        return skip + 1;
      };

      const baseListRequest: CatalogItemListRequest & { page: number } = {
        ...props.listRequest,
        pagination: 1,
        page:
          typeof props.listRequest.page === 'number' && props.listRequest.page > 1
            ? calcPage(props.listRequest.page, props.listRequest.pagination ?? 1)
            : 1,
      };

      if (props.id) {
        const index = response.items.findIndex((item) => item.id === props.id);
        if (index >= 0) {
          baseListRequest.page += index;
        }
      }

      const newItemsData: CatalogItemViewListItemData[] = Array.from(new Array(response.totalCount - baseListRequest.page + 1), (_, i) => ({
        catalogItemType: baseListRequest.type,
        listRequest: {
          ...baseListRequest,
          page: baseListRequest.page + i,
        },
      }));

      setItemsData(newItemsData);
    })();

    return () => {
      isEffectActive = false;
    };
  }, [dispatch, props.id, props.listRequest]);

  if (!itemsData) {
    return null;
  }

  return (
    <CatalogItemModalView
      {...props}
      viewProps={{
        itemsData,
        itemComponent: CatalogItemViewListItem,
        showInstruction: !clientProfile ? !isInstructionCompleted : !isInstructionCompletedByRegisteredUser,
        onInstructionCompleted: handleInstructionCompleted,
        onLike: createAssessmentHandler('like'),
        onDislike: createAssessmentHandler('dislike'),
      }}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const CatalogItemView = <DataType extends unknown>(props: CatalogItemViewProps<DataType>): ReturnType<React.FC> => {
  const dispatch = useDispatch();
  const { id, catalogType } = useCatalogItem();
  const isAnyModalOpened = useIsAnyModalOpen();
  const closeCatalogItem = useCallback(() => {
    dispatch(catalogItemSlice.actions.setCatalogItem(null));
  }, [dispatch]);

  useEffect(() => {
    if (id && isAnyModalOpened) {
      closeCatalogItem();
    }
  }, [closeCatalogItem, id, isAnyModalOpened]);

  useEffect(() => closeCatalogItem, []);

  const addProps: CatalogItemViewPropsBase<unknown> = {
    id: id || 0,
    isOpen: !!id,
    onClose: closeCatalogItem,
    catalogItemType: catalogType || CatalogItemType.Resume,
  };

  if (props.type === CatalogItemModalViewType.List) {
    return <CatalogItemViewList {...props} {...addProps} />;
  } else if (props.type === CatalogItemModalViewType.Single) {
    return <CatalogItemViewSingle {...props} {...addProps} />;
  } else {
    throw new Error(`[CatalogItemView]: type ${(props as CatalogItemViewProps<DataType>).type} is not supported!`);
  }
};
